/**
 * usePhysicalCheckout — hook con toda la lógica de estado + handlers del
 * PhysicalCheckoutModal (SPEC-244 · §Sprint5 partición VERBATIM de
 * `frontend/src/components/market/checkout-modal.tsx` 2.637 L).
 *
 * El monolito original mezclaba estado + handlers + render en un único
 * componente de ~1.247 L. Este hook extrae VERBATIM toda la lógica de:
 *   - State declarations (step, quantity, fulfillmentMethod,
 *     shippingAddress, savedAddresses, selectedSavedAddressId,
 *     indicaciones, saveThisAddress, splitPayment, addressLabel,
 *     savedAddressesLoading, savedAddressesFetched, pickupConfirmed,
 *     variationSelection, submitting, submitError)
 *     [2FA state removed per Founder directive 2026-11: checkout no
 *     longer requires 2FA — no marketplace requires 2FA to buy]
 *   - INV-SEC-03 idempotencyKeyRef (safeUUID, reused within a modal
 *     session — fresh UUID on each open)
 *   - Founder directive 2026-10-05 — effectiveShips / effectivePickup
 *     resolution (product's own modalities/config first, store fallback)
 *   - useEffect reset on `open` change
 *   - useEffect fetch saved addresses on open (Sprint 139 PR-H)
 *   - handleSelectSavedAddress (pre-fills shippingAddress state)
 *   - maybeSaveAddressForNextTime (POST /api/me/addresses on success when
 *     buyer checked "Guardar esta dirección" while typing a NEW address)
 *   - variation + hasVariations derived values
 *   - detailsValid useMemo (Step 1 validation)
 *   - callCreate (POST /api/market/orders with idempotencyKey; SPEC-157
 *     SINGLE_PURCHASE_ALREADY_BOUGHT intercept)
 *   - estimate useMemo (UX-affordance total)
 *   - pickupInfo / pickupLoc / mapsLink derived values (Founder directive
 *     2026-10-05)
 *   - handleOpenChange (anti-P-043 — block close during submit)
 *
 * Transformaciones documentadas (las ÚNICAS):
 *   - T-HOOK-EXTRACTION: la lógica se mueve del cuerpo del componente a un
 *     hook personalizado `usePhysicalCheckout({ open, onClose, product,
 *     sellerFulfillment })` que retorna todo el state + handlers.
 *   - T-IMPORTS: recalcula imports (solo lo que el hook usa).
 *
 * §6 Anti-Invención: NO business logic. Anti-P-371: cero cambios.
 * Anti-P-041: 0 `as any` (the `as Route` cast is in the view, not here).
 * Anti-P-042: 0 `console.*`. Anti-P-043: submitting state blocks close.
 */

'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { toastSuccess } from '@/lib/toast-premium';
import { api, ApiClientError } from '@/lib/api-client';
import { safeUUID } from '@/lib/utils';
import type { Route } from 'next';
import type {
  PublicProductResponse,
  OrderCreateRequestPhysical,
  OrderResponse,
  FulfillmentMethod,
  ShippingAddress,
  VariationSelection,
  // Sprint 139 (PR-H: Saved Shipping Addresses)
  SavedAddressResponse,
} from '@profileia/shared';
import { resolveShipping, resolvePickupInfo, estimateOrder, mapCreateError } from '../helpers';
import type { Step } from '../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UsePhysicalCheckoutArgs {
  open: boolean;
  onClose: () => void;
  product: PublicProductResponse;
  sellerFulfillment: { ships: boolean; pickup: boolean };
}

// ─── Hook ───────────────────────────────────────────────────────────────────

export function usePhysicalCheckout({
  open,
  onClose,
  product,
  sellerFulfillment,
}: UsePhysicalCheckoutArgs) {
  const router = useRouter();
  const [step, setStep] = useState<Step>('details');
  const [quantity, setQuantity] = useState(1);
  const [fulfillmentMethod, setFulfillmentMethod] = useState<FulfillmentMethod | null>(null);
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    name: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    zip: '',
  });
  // Sprint 139 (PR-H: Saved Shipping Addresses, Founder directive
  // 2026-09-25 — "que el usuario pueda guardar su dirección para
  // prellenado automatico como en mercadolibre"):
  // - savedAddresses: array fetched from /api/me/addresses on modal open
  // - selectedSavedAddressId: 'new' for new address, OR a savedAddressId UUID
  // - indicaciones: free-text delivery instructions (NEW field on ShippingAddress)
  // - saveThisAddress: when true + selectedSavedAddressId==='new', POST the
  //   new address to /api/me/addresses after order creation (so the next
  //   checkout pre-fills it)
  const [savedAddresses, setSavedAddresses] = useState<SavedAddressResponse[]>([]);
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState<string>('new');
  const [indicaciones, setIndicaciones] = useState('');
  const [saveThisAddress, setSaveThisAddress] = useState(false);
  // Sprint 139 (PR-O): split payment for services
  const [splitPayment, setSplitPayment] = useState(false);
  const [addressLabel, setAddressLabel] = useState(''); // for "Guardar como: Casa, Trabajo, etc."
  const [savedAddressesLoading, setSavedAddressesLoading] = useState(false);
  const [savedAddressesFetched, setSavedAddressesFetched] = useState(false);
  const [pickupConfirmed, setPickupConfirmed] = useState(false);
  const [variationSelection, setVariationSelection] = useState<VariationSelection | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // INV-SEC-03: idempotency key generated ONCE per modal open. Retries
  // within the same modal session reuse the key (so a network hiccup
  // doesn't double-debit). A new modal open generates a fresh UUID.
  const idempotencyKeyRef = useRef<string>(safeUUID());

  // Founder directive 2026-10-05 — per-product fulfillment resolution
  // (defense in depth: the product's own modalities/config decide what the
  // checkout offers; the parent's sellerFulfillment prop is a stale-data
  // guard ONLY for inheritSeller products).
  //   - inheritSeller=true  → store flags (both sources must agree).
  //   - inheritSeller=false → the product's modalities decide FREELY; a
  //     modality is offered when its EFFECTIVE config resolves (product
  //     config first, else the store's — e.g. a cabin product with its own
  //     pickup address offers pickup even when the store doesn't).
  const effectiveShips = product.fulfillment.inheritSeller
    ? product.seller.fulfillmentOptions.ships && sellerFulfillment.ships
    : resolveShipping(product) !== null;
  const effectivePickup = product.fulfillment.inheritSeller
    ? product.seller.fulfillmentOptions.pickup && sellerFulfillment.pickup
    : resolvePickupInfo(product) !== null;

  // Reset state every time the modal opens.
  useEffect(() => {
    if (open) {
      setStep('details');
      setQuantity(1);
      // Default to 'ship' if both are available (most common case); else
      // the only available method; else null (the user can't proceed).
      setFulfillmentMethod(
        effectiveShips ? 'ship' : effectivePickup ? 'pickup' : null,
      );
      setShippingAddress({
        name: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        zip: '',
      });
      // Sprint 139 (PR-H): reset saved-address state on modal open.
      setSelectedSavedAddressId('new');
      setIndicaciones('');
      setSaveThisAddress(false);
      setAddressLabel('');
      setPickupConfirmed(false);
      // Pre-select the first in-stock variation option (if applicable) so
      // the user doesn't have to explicitly pick — they can change it.
      const firstVariation = product.variations[0];
      if (firstVariation) {
        const firstInStock = firstVariation.options.find((o) => o.stock > 0);
        if (firstInStock) {
          setVariationSelection({
            name: firstVariation.name,
            option: firstInStock.name,
          });
        } else {
          setVariationSelection(null);
        }
      } else {
        setVariationSelection(null);
      }
      setSubmitting(false);
      setSubmitError(null);
      idempotencyKeyRef.current = safeUUID();
    }
  }, [open, effectiveShips, effectivePickup, product.variations]);

  // Sprint 139 (PR-H: Saved Shipping Addresses): fetch the user's saved
  // addresses when the modal opens AND the user is going to ship (so we
  // can offer the address book selector). We fetch once per modal session
  // (the savedAddressesFetched guard prevents re-fetching on every state
  // change). If the fetch fails (network, 401, etc.), we silently fall
  // back to the manual form (savedAddresses stays empty → selector hidden).
  useEffect(() => {
    if (!open) {
      // Reset the fetched flag on close so the next open re-fetches.
      setSavedAddressesFetched(false);
      return;
    }
    if (savedAddressesFetched) return;
    setSavedAddressesFetched(true);
    setSavedAddressesLoading(true);
    api.userAddresses
      .list()
      .then((addresses) => {
        setSavedAddresses(addresses);
        // If the user has at least 1 saved address, default-select the
        // first one (so the buyer can complete checkout faster). If 0,
        // default to 'new' (already set in the modal-open reset above).
        if (addresses.length > 0) {
          setSelectedSavedAddressId(addresses[0].savedAddressId);
          // Pre-fill the shippingAddress state from the selected saved address
          // (so the form below shows the values, AND if the buyer switches
          // to 'new' the form is cleared).
          const first = addresses[0];
          setShippingAddress({
            name: first.name,
            phone: first.phone,
            address: first.address,
            city: first.city,
            country: first.country,
            zip: first.zip,
            indicaciones: first.indicaciones ?? '',
          });
          setIndicaciones(first.indicaciones ?? '');
        }
      })
      .catch(() => {
        // Silent fail — the buyer can still type the address manually.
        // We don't show an error toast because the address book is an
        // enhancement, not a required feature.
      })
      .finally(() => setSavedAddressesLoading(false));
  }, [open, savedAddressesFetched]);

  // Sprint 139 (PR-H): handler when the user selects a saved address from
  // the radio list OR switches to 'new'. Pre-fills the shippingAddress
  // state from the selected saved address (or clears it for 'new').
  const handleSelectSavedAddress = useCallback(
    (savedAddressId: string) => {
      setSelectedSavedAddressId(savedAddressId);
      if (savedAddressId === 'new') {
        // Clear the form so the buyer can type a new address.
        setShippingAddress({
          name: '',
          phone: '',
          address: '',
          city: '',
          country: '',
          zip: '',
        });
        setIndicaciones('');
        setSaveThisAddress(false);
        setAddressLabel('');
      } else {
        // Find the selected saved address + pre-fill the form.
        const selected = savedAddresses.find((a) => a.savedAddressId === savedAddressId);
        if (selected) {
          setShippingAddress({
            name: selected.name,
            phone: selected.phone,
            address: selected.address,
            city: selected.city,
            country: selected.country,
            zip: selected.zip,
            indicaciones: selected.indicaciones ?? '',
          });
          setIndicaciones(selected.indicaciones ?? '');
          // When using an existing saved address, don't re-save it.
          setSaveThisAddress(false);
          setAddressLabel('');
        }
      }
    },
    [savedAddresses],
  );

  // Sprint 139 (PR-H): after the order is created, if the buyer checked
  // "Guardar esta dirección" while typing a NEW address (not selecting
  // from the saved list), POST the new address to /api/me/addresses so
  // the next checkout pre-fills it. Silent failure — the address book is
  // an enhancement, not a critical feature (the order was already
  // created successfully). NO 2FA prompt here — the user already
  // authenticated 2FA for the order creation; the savedAddress POST will
  // return 403 2FA_REQUIRED if the 2FA window expired, which we silent-skip
  // (the user can save the address manually later from /app/settings).
  const maybeSaveAddressForNextTime = useCallback(async () => {
    if (!saveThisAddress) return;
    if (selectedSavedAddressId !== 'new') return;
    if (!addressLabel.trim()) return;
    // Build the SavedAddressInput from the current shippingAddress state.
    try {
      await api.userAddresses.add({
        label: addressLabel.trim(),
        name: shippingAddress.name,
        phone: shippingAddress.phone,
        address: shippingAddress.address,
        city: shippingAddress.city,
        country: shippingAddress.country,
        zip: shippingAddress.zip,
        indicaciones: indicaciones.trim() || null,
      });
      // No success toast — the buyer is already being redirected to the
      // order detail page. The saved address will appear in the next
      // checkout's address book selector.
    } catch {
      // Silent fail — don't block the redirect. The buyer can save the
      // address manually later from /app/settings if they want.
    }
  }, [
    saveThisAddress,
    selectedSavedAddressId,
    addressLabel,
    shippingAddress,
    indicaciones,
  ]);

  // ─── Validation ──────────────────────────────────────────────────────────

  const variation = product.variations[0];
  const hasVariations = !!variation;

  // Step 1 validation — can the user proceed to Step 2?
  const detailsValid = useMemo(() => {
    if (fulfillmentMethod === null) return false;
    if (fulfillmentMethod === 'ship') {
      // All 6 shipping address fields required + non-empty (Zod `.min(1)`
      // on `shippingAddressSchema`).
      //
      // BUG FIX v124.2 (PR #1429): previously used `Object.values(shippingAddress).every(...)`
      // which ALSO checked `indicaciones` (an OPTIONAL field per the UI label
      // "(opcional)" + per `shippingAddressSchema.indicaciones.optional()`).
      // When `indicaciones` was present in state (either from a saved address
      // load OR from the buyer typing in the Indicaciones textarea) and set
      // to empty string '', the validation would fail — forcing the buyer to
      // type something in the "optional" field to enable the Continue button.
      // Now we explicitly check only the 6 required fields, leaving
      // `indicaciones` as truly optional (consistent with the UI + Zod schema).
      const { name, phone, address, city, country, zip } = shippingAddress;
      return (
        name.trim().length > 0 &&
        phone.trim().length > 0 &&
        address.trim().length > 0 &&
        city.trim().length > 0 &&
        country.trim().length > 0 &&
        zip.trim().length > 0
      );
    }
    if (fulfillmentMethod === 'pickup') {
      // The "Confirmo que pasaré a recoger" checkbox must be checked
      // (the user acknowledges they'll physically collect the order).
      return pickupConfirmed;
    }
    return false;
  }, [fulfillmentMethod, shippingAddress, pickupConfirmed]);

  // ─── Submit handlers ─────────────────────────────────────────────────────

  const callCreate = async () => {
    if (fulfillmentMethod === null) {
      setSubmitError('Selecciona un método de entrega antes de continuar.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Fase 5 — send the explicit `productType: 'physical'` discriminator
      // (Task B2 controller defaults to 'physical' when missing — but
      // explicit is safer for forward-compat with the union schema).
      const payload: OrderCreateRequestPhysical = {
        productType: 'physical',
        productId: product.productId,
        quantity,
        fulfillmentMethod,
      };
      if (fulfillmentMethod === 'ship') {
        payload.shippingAddress = shippingAddress;
      }
      if (variationSelection) {
        payload.variationSelections = [variationSelection];
      }

      const result: OrderResponse = await api.market.orders.create(payload, {
        idempotencyKey: idempotencyKeyRef.current,
      });

      // Sprint 139 (PR-H): if the buyer checked "Guardar esta dirección"
      // while typing a NEW shipping address, persist it to their address
      // book for next time. Silent failure — the order was already
      // created; the address book save is an enhancement.
      void maybeSaveAddressForNextTime();

      // Success — toast + redirect to the order detail page.
      toastSuccess.created('la orden');
      // CM-51 (typedRoutes): cast the dynamic href to `Route`. The
      // `/market/ordenes/[orderNumber]` route exists in the App Router;
      // the cast bypasses the type-checker that doesn't yet know about
      // this dynamic segment (same pattern as `/market/tienda/[storeSlug]`
      // casts elsewhere in the codebase).
      router.push(`/market/ordenes/${result.orderNumber}` as Route);
      // Close the modal so the back-button doesn't return to the product
      // page with the modal still open.
      onClose();
    } catch (err) {
      // SPEC-157: intercept 409 SINGLE_PURCHASE_ALREADY_BOUGHT — this is
      // NOT an error state, the buyer is an existing customer who needs
      // to coordinate recurring payments off-platform. Show a friendly
      // info toast + close the modal (no inline error).
      if (err instanceof ApiClientError && err.code === 'SINGLE_PURCHASE_ALREADY_BOUGHT') {
        toast.info('Ya eres cliente de este servicio', {
          description: 'Contacta directamente al vendedor para gestionar tus pagos recurrentes.',
          duration: 6000,
        });
        onClose();
        return;
      }
      if (err instanceof ApiClientError) {
        const mapped = mapCreateError(err);
        if (mapped !== null) {
          setSubmitError(mapped);
          if (err.code === 'IDEMPOTENCY_CONFLICT') {
            idempotencyKeyRef.current = safeUUID();
          }
        } else {
          setSubmitError(err.message ?? 'Error al crear la orden.');
        }
      } else if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      } else {
        setSubmitError('Error al crear la orden. Intenta de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Derived display values ─────────────────────────────────────────────

  const estimate = useMemo(
    () =>
      estimateOrder(product, variationSelection, quantity, fulfillmentMethod),
    [product, variationSelection, quantity, fulfillmentMethod],
  );

  // Founder directive 2026-10-05 — the checkout shows the EFFECTIVE pickup
  // address: the product's own (config.pickup) when customized, else the
  // store's pickupConfig (default).
  const pickupInfo = resolvePickupInfo(product);
  const pickupLoc = pickupInfo
    ? { lat: pickupInfo.lat, lng: pickupInfo.lng, address: pickupInfo.address }
    : null;
  const mapsLink = pickupLoc
    ? `https://www.google.com/maps?q=${pickupLoc.lat},${pickupLoc.lng}`
    : null;

  // Disable the modal's close affordance while submitting (anti-P-043 —
  // the user can't navigate away mid-create and lose track of the
  // idempotency key).
  const handleOpenChange = (o: boolean) => {
    if (!o && submitting) return; // block close during submit
    if (!o) onClose();
  };

  return {
    // state
    step,
    setStep,
    quantity,
    setQuantity,
    fulfillmentMethod,
    setFulfillmentMethod,
    shippingAddress,
    setShippingAddress,
    savedAddresses,
    selectedSavedAddressId,
    setSelectedSavedAddressId,
    indicaciones,
    setIndicaciones,
    saveThisAddress,
    setSaveThisAddress,
    splitPayment,
    setSplitPayment,
    addressLabel,
    setAddressLabel,
    savedAddressesLoading,
    pickupConfirmed,
    setPickupConfirmed,
    variationSelection,
    setVariationSelection,
    submitting,
    submitError,
    setSubmitError,
    // derived
    effectiveShips,
    effectivePickup,
    variation,
    hasVariations,
    detailsValid,
    estimate,
    pickupInfo,
    pickupLoc,
    mapsLink,
    // handlers
    handleSelectSavedAddress,
    callCreate,
    handleOpenChange,
  };
}
